import type { ReactElement } from 'react';
import { forwardRef } from 'react';

import type { TLoadingColor } from '..';
import { createPadding } from '../ui-utils';
import { Icon, Loading } from '..';

import type { IButtonProps } from './button.interface';

import './button.scss';

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      color,
      variant,
      disabled = false,
      fullWidth = false,
      loading = false,
      type = 'button',
      direction = 'row',
      icon,
      children,
      size = 'medium',
      rounded = 'full',
      ariaLabel,
      pl = 16,
      pr = 16,
      pt = 0,
      pb = 0,
      className = '',
      ...props
    },
    ref
  ): ReactElement => {
    const buttonClass = {
      disabledContained:
        'co-bg-disabled-light co-text-disabled-main co-border-disabled-light',
      disabledOutlined:
        'co-bg-transparent co-border-disabled-main co-text-disabled-main co-border-1',
      outlined: `co-bg-transparent co-text-${color}-main co-border-${color}-main co-border-1`,
      contained: `co-bg-${color}-main co-text-${color}-text hover:co-bg-${color}-dark co-border-${color}-main`,
      disabledLoadingOutlined: `co-bg-transparent co-text-${color}-main co-border-${color}-main co-border-1`,
      disabledLoadingContained: `co-bg-${color}-main co-text-${color}-text hover:co-bg-${color}-dark co-border-${color}-main`,
      gray: 'co-bg-gray-xlight co-text-gray-main co-border-gray-xlight'
    };
    const getButtonClass = (): string => {
      if (disabled && loading) {
        if (variant === 'contained') {
          return buttonClass.disabledContained;
        }
        return buttonClass.disabledLoadingContained;
      }
      if (disabled && variant === 'contained') {
        return buttonClass.disabledContained;
      }

      if (disabled && variant === 'outlined') {
        return buttonClass.disabledOutlined;
      }

      if (variant === 'contained') {
        if (color === 'gray') {
          return buttonClass.gray;
        }
        return buttonClass.contained;
      }

      if (variant === 'outlined') {
        if (color === 'gray') {
          return buttonClass.gray;
        }
        return buttonClass.outlined;
      }

      return '';
    };

    const createIconColor = (): TLoadingColor => {
      if (color && variant === 'outlined') return color as TLoadingColor;
      return !color ? 'black' : 'white';
    };

    const createSizeIcon = (): number => {
      if (size === 'medium') return 24;
      if (size === 'large') return 32;
      return 16;
    };
    const createSizeButton = (): string => {
      if (size === 'large') return 'co-h-56px co-text-body1';
      if (size === 'medium') return 'co-h-48px co-text-body1';
      return 'co-h-40px co-text-body2';
    };

    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        type={type}
        disabled={disabled}
        className={`
                ${getButtonClass()}
                ${fullWidth ? 'co-w-full' : 'co-w-auto co-min-w-45'}
                ${createSizeButton()}
                co-flex-${'-' + direction}
                ${
                  disabled || loading
                    ? 'co-pointer-events-none'
                    : 'co-pointer-events-auto'
                }
                ${createPadding({ pl, pr, pt, pb })}
                co-rounded-${rounded}
                co-flex
                co-items-center
                co-justify-center
                co-gap-2
                co-focus:outline-none
                co-font-montserrat
                co-font-bold
                co-button-ui
                ${className}
                `}
        {...props}
      >
        {loading ? (
          <Loading color={createIconColor()} variant="dots" />
        ) : (
          <>
            {icon && (
              <Icon
                width={createSizeIcon()}
                height={createSizeIcon()}
                color={createIconColor()}
                icon={icon}
              />
            )}
            {children}
          </>
        )}
      </button>
    );
  }
);
